<template>
  <Page>
    <div class="space-y-6">

      <!-- Page Header -->
      <section id="project-settings-header" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex items-start">
            <button
              @click="router.back()"
              class="mr-4 mt-1 rounded-full p-2 text-secondary transition-colors hover:bg-primary/10 hover:text-primary"
              aria-label="Go back"
            >
              <i class="fa-solid fa-arrow-left"></i>
            </button>
            <div class="flex items-center">
              <div class="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                <i :class="project?.icon || 'fa-solid fa-sliders'" class="text-primary"></i>
              </div>
              <div>
                <h1 class="text-2xl font-bold text-text">
                  {{ isLoading ? 'Loading...' : (project?.name || 'Project Settings') }}
                </h1>
                <p class="text-text-light">Configure settings for this project</p>
              </div>
            </div>
          </div>
          <div v-if="project" class="flex items-center space-x-2">
            <Tag :value="project.status || 'Unknown'" :severity="getStatusSeverity(project.status)" class="font-medium" />
          </div>
        </div>
      </section>

      <!-- Access Denied State -->
      <section v-if="isAccessDenied" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="text-center py-8">
          <i class="fa-solid fa-lock text-4xl text-warning mb-4"></i>
          <h3 class="text-lg font-medium text-text mb-2">Access Restricted</h3>
          <p class="text-text-light mb-4">
            Project Settings is only accessible to System Administrators and Project Owners.
          </p>
          <Button label="Go to Dashboard" icon="pi pi-home" @click="router.push('/home')" />
        </div>
      </section>

      <!-- No Project Selected State -->
      <section v-else-if="!selectedProjectId" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="text-center py-8">
          <i class="fa-solid fa-folder-open text-4xl text-text-light mb-4"></i>
          <h3 class="text-lg font-medium text-text mb-2">No Project Selected</h3>
          <p class="text-text-light mb-4">Please select a project to view its settings.</p>
          <Button label="Select a Project" icon="pi pi-folder-open" @click="router.push('/home/project-selection')" />
        </div>
      </section>

      <!-- Loading State -->
      <section v-else-if="isLoading" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="animate-pulse space-y-4">
          <div class="h-4 bg-border rounded w-1/3"></div>
          <div class="h-3 bg-border rounded w-1/2"></div>
          <div class="h-3 bg-border rounded w-2/5"></div>
        </div>
      </section>

      <!-- Error State -->
      <section v-else-if="error" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="text-center py-8">
          <i class="fa-solid fa-exclamation-triangle text-4xl text-warning mb-4"></i>
          <h3 class="text-lg font-medium text-text mb-2">Failed to load project</h3>
          <p class="text-text-light mb-4">{{ error }}</p>
          <Button label="Retry" icon="pi pi-refresh" @click="fetchProject" />
        </div>
      </section>

      <!-- Project Content -->
      <section v-else-if="project" class="space-y-6">

        <!-- Project Information Card -->
        <div class="bg-background-card rounded-lg shadow-card p-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-semibold text-text">Project Information</h2>
              <p class="text-sm text-text-light mt-1">General details and metadata for this project.</p>
            </div>
            <div class="flex items-center space-x-2">
              <Button
                v-if="!projectEditing.editingState.value.isEditing"
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                outlined
                @click="projectEditing.startEditing(project)"
                class="text-sm"
              />
              <div v-else class="flex items-center space-x-2">
                <Button
                  label="Save"
                  icon="pi pi-check"
                  size="small"
                  @click="handleSaveProject"
                  :loading="projectEditing.loading.value"
                  :disabled="!projectEditing.isValid.value"
                  class="text-sm"
                />
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  size="small"
                  outlined
                  @click="projectEditing.cancelEditing"
                  :disabled="projectEditing.loading.value"
                  class="text-sm"
                />
              </div>
            </div>
          </div>

          <!-- Basic Info Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <!-- Project Name -->
            <div>
              <dt class="text-sm font-medium text-text-light mb-1">Project Name</dt>
              <dd v-if="!projectEditing.editingState.value.isEditing" class="text-text font-medium">
                {{ project.name || 'Untitled Project' }}
              </dd>
              <InputText
                v-else
                :model-value="projectEditing.editingState.value.editableFields.name"
                @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('name', value); }"
                placeholder="Enter project name..."
                class="w-full"
              />
            </div>

            <!-- Project Status -->
            <div>
              <dt class="text-sm font-medium text-text-light mb-1">Status</dt>
              <dd v-if="!projectEditing.editingState.value.isEditing">
                <Tag :value="project.status" :severity="getStatusSeverity(project.status)" class="font-medium" />
              </dd>
              <Select
                v-else
                :model-value="projectEditing.editingState.value.editableFields.status"
                @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('status', value); }"
                :options="[
                  { label: 'Active', value: 'Active' },
                  { label: 'Archived', value: 'Archived' },
                  { label: 'Template', value: 'Template' }
                ]"
                option-label="label"
                option-value="value"
                placeholder="Select status"
                class="w-full"
              />
            </div>
          </div>

          <!-- Description -->
          <div class="mb-6">
            <dt class="text-sm font-medium text-text-light mb-1">Description</dt>
            <dd v-if="!projectEditing.editingState.value.isEditing" class="text-text">
              {{ project.description || 'No description available' }}
            </dd>
            <Textarea
              v-else
              :model-value="projectEditing.editingState.value.editableFields.description"
              @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('description', value); }"
              placeholder="Enter project description..."
              rows="3"
              class="w-full rounded-lg"
            />
          </div>

          <!-- Metadata -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <dt class="text-sm font-medium text-text-light mb-1">Created</dt>
              <dd class="text-text">{{ formatCreatorModifier(project.createdBy, project.createdAt, 'Created') }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-text-light mb-1">Last Modified</dt>
              <dd class="text-text">{{ formatCreatorModifier(project.lastModifiedBy, project.lastModifiedAt, 'Last Modified') }}</dd>
            </div>
          </div>

          <!-- Edit Error -->
          <div
            v-if="projectEditing.error.value"
            class="mt-4 p-3 bg-error/10 border border-error/20 text-error rounded-lg"
          >
            <div class="flex items-center">
              <i class="fa-solid fa-exclamation-triangle mr-2"></i>
              <span class="text-sm">{{ projectEditing.error.value }}</span>
            </div>
          </div>
        </div>

        <!-- Project Policies -->
        <div class="bg-background-card rounded-lg shadow-card p-6">
          <div class="flex items-center gap-3 mb-4">
            <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <i class="fa-solid fa-shield-halved text-primary text-sm"></i>
            </div>
            <div>
              <h2 class="text-lg font-semibold text-text">Project Policies</h2>
              <p class="text-sm text-text-light mt-0.5">Configure policies that govern project workflows and data operations.</p>
            </div>
          </div>

          <LoadSheetCommitPolicyCard />
        </div>

        <!-- Factory Holidays -->
        <div class="bg-background-card rounded-lg shadow-card p-6">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <i class="fa-solid fa-calendar-day text-primary text-sm"></i>
              </div>
              <div>
                <h2 class="text-lg font-semibold text-text">Factory Holidays</h2>
                <p class="text-sm text-text-light mt-0.5">Maintain non-working days that drive maintenance schedule working-day shifting.</p>
              </div>
            </div>
            <Button
              label="Manage Holidays"
              icon="fa-solid fa-arrow-right"
              icon-pos="right"
              outlined
              @click="router.push('/project-administration/factory-holidays')"
            />
          </div>
        </div>

      </section>

    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useProject } from '@/features/project-management/composables/useProject';
import { useProjectEditing } from '@/features/project-management/composables/useProjectEditing';
import { formatCreatorModifier } from '@/features/project-management/utils/metadataFormatters';
import { useProjectStore } from '@/shared/stores/project';
import { useUserRole } from '@/shared/composables/useUserRole';
import { SystemRole, ProjectRole } from '@/core/types/models/RoleTypes';
import { addToast } from '@/shared/utils/toastMessages';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Page from '@/shared/components/layouts/Page.vue';
import LoadSheetCommitPolicyCard from '@/features/project-administration/components/LoadSheetCommitPolicyCard.vue';

definePage({
  meta: {
    requiresProject: true,
  },
});

const router = useRouter();
const toast = useToast();
const projectStore = useProjectStore();
const { userRole } = useUserRole();

const { project, isLoading, error, loadProject } = useProject();
const projectEditing = useProjectEditing();

const selectedProjectId = computed(() => projectStore.selectedProjectId);

const isAuthorized = computed(() => {
  const role = userRole.value;
  return role === SystemRole.Administrator || role === ProjectRole.ProjectOwner;
});

const isAccessDenied = ref(false);

async function fetchProject(): Promise<void> {
  if (!selectedProjectId.value) return;
  await loadProject(selectedProjectId.value);
}

function getStatusSeverity(status?: string): string {
  switch (status) {
    case 'Active': return 'success';
    case 'Archived': return 'secondary';
    case 'Pending': return 'warning';
    case 'Template': return 'info';
    default: return 'secondary';
  }
}

async function handleSaveProject(): Promise<void> {
  if (!project.value?.id) return;

  const updatedProject = await projectEditing.saveChanges(project.value.id);
  if (updatedProject) {
    project.value = updatedProject;
    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: 'Project settings saved successfully',
    });
  } else if (projectEditing.error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Error',
      detail: projectEditing.error.value,
    });
  }
}

onMounted(async () => {
  if (!selectedProjectId.value) return;

  if (!isAuthorized.value) {
    isAccessDenied.value = true;
    return;
  }

  await fetchProject();
});
</script>
